import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ResponsiveDialog from '../common/ResponsiveDialog';
import e2iApi from '../../services/e2iApi';
import { showSnackbar } from '../../store/slices/uiSlice';
import type { Attachment, AttachmentEntity } from '../../types/e2i';
import { ATTACHMENT_ACCEPT, MAX_ATTACHMENT_BYTES, blobErrorText, errorText, fileSizeText, formatDateTime } from '../../utils/e2i';

/** Opens a stored file in a new tab (a download when the browser blocks the tab). */
export async function openAttachment(a: Attachment, onError: (message: string) => void): Promise<void> {
  const w = window.open('', '_blank');
  try {
    const blob = await e2iApi.downloadAttachment(a.attachmentId);
    const url = URL.createObjectURL(blob);
    if (w) {
      w.location.href = url;
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = a.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e: any) {
    if (w) w.close();
    onError(await blobErrorText(e, 'The file could not be opened'));
  }
}

interface Props {
  id: string;
  entity: AttachmentEntity;
  entityId: number | null;
  attachments: Attachment[];
  canUpload: boolean;
  canDelete: boolean;
  /** Reloads what shows the files once one is added or removed. */
  onChanged: () => Promise<void> | void;
  title?: string;
  emptyText?: string;
}

/** The files of an enquiry or an invoice request: open, add (photos and PDFs, 10 MB), remove. */
const AttachmentsPanel: React.FC<Props> = ({ id, entity, entityId, attachments, canUpload, canDelete, onChanged, title = 'Files', emptyText = 'No files yet.' }) => {
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<Attachment | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const fail = (message: string) => dispatch(showSnackbar({ message, severity: 'error' }));

  const upload = async (files: FileList | null) => {
    if (!files?.length || !entityId) return;
    const list = Array.from(files);
    const tooBig = list.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) { fail(`${tooBig.name} is larger than 10 MB`); return; }
    setUploading(true);
    try {
      for (const f of list) await e2iApi.uploadAttachment(entity, entityId, f);
      dispatch(showSnackbar({ message: list.length === 1 ? `${list[0].name} attached` : `${list.length} files attached`, severity: 'success' }));
    } catch (e: any) {
      fail(errorText(e, 'The file could not be attached'));
    } finally {
      setUploading(false);
      await onChanged();
    }
  };

  const remove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      await e2iApi.deleteAttachment(removing.attachmentId);
      setRemoving(null);
      await onChanged();
    } catch (e: any) {
      fail(errorText(e, 'The file could not be removed'));
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle2" component="h3" sx={{ mr: 'auto' }}>{title}</Typography>
        {canUpload && entityId ? (
          <Button component="label" size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />} disabled={uploading}>
            {uploading ? 'Attaching…' : 'Attach files'}
            <input
              hidden
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              aria-label={`${title}: attach photos or PDFs`}
              onChange={(e) => { void upload(e.target.files); e.target.value = ''; }}
            />
          </Button>
        ) : null}
      </Stack>
      {attachments.length ? (
        <Stack component="ul" spacing={0.5} sx={{ m: 0, p: 0, listStyle: 'none' }} id={id}>
          {attachments.map((a) => (
            <Stack component="li" key={a.attachmentId} direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Button size="small" variant="text" onClick={() => { void openAttachment(a, fail); }} sx={{ textAlign: 'left', minWidth: 0, wordBreak: 'break-all' }}>
                {a.fileName}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 auto', minWidth: 0 }}>
                {[fileSizeText(a.size), a.uploadedByName, formatDateTime(a.uploadedAt)].filter(Boolean).join(' · ')}
              </Typography>
              {canDelete ? (
                <IconButton size="small" aria-label={`Remove ${a.fileName}`} onClick={() => setRemoving(a)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
      )}

      <ResponsiveDialog open={!!removing} onClose={removeBusy ? undefined : () => setRemoving(null)} maxWidth="xs" fullWidth mobileFullScreen={false} aria-labelledby={`${id}-remove-title`}>
        <DialogTitle id={`${id}-remove-title`}>Remove this file?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{removing?.fileName}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRemoving(null)} disabled={removeBusy}>Keep</Button>
          <Button color="error" onClick={() => { void remove(); }} disabled={removeBusy}>{removeBusy ? 'Removing…' : 'Remove'}</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default AttachmentsPanel;
